/**
 * 161: every React Query key the admin panel uses for a resource, named once.
 *
 * Before this module the keys were literals at each call site, and two resources were spelled two
 * ways: `['locations']` in `CarFormPage` against `['admin-locations']` in `LocationListPage`, and
 * `['pricing-templates']` against `['admin-pricing']`. Both pairs issue the *same* request, so each
 * resource was fetched twice, cached twice, and invalidating one spelling left the other stale.
 * Nothing failed; the panel just showed one screen's answer on one screen and another screen's
 * answer on another.
 *
 * 🔴 `list` is a PREFIX, not necessarily a whole key. Screens that paginate or filter build on it —
 * `[...RESOURCE_KEYS.car.list, page, plateQuery]` — and React Query's partial matching means
 * invalidating the prefix reaches every page and filter combination at once. That is why the prefix
 * is what a save invalidates.
 *
 * 🔴 A record key is NOT generally under its list prefix (`['car', id]` does not begin with
 * `['admin-cars']`). Do not rely on one invalidation covering both; `useSaveMutation` invalidates
 * them separately, deliberately.
 */

export interface ResourceKeys {
  /** The prefix every read of this resource's collection is built on. */
  readonly list: readonly [string];
  /** The key one record of this resource is cached under. */
  readonly one: (id: string) => readonly unknown[];
}

/**
 * Values here are byte-identical to the literals in use before feature 161, with two deliberate
 * exceptions — `maintenance` and the four taxonomy resources — explained at their entries.
 * Anything else that moves silently splits a cache in two, which is the defect this module exists
 * to prevent. `tests/services/resourceKeys.test.ts` pins every value for that reason.
 */
export const RESOURCE_KEYS = {
  car: { list: ['admin-cars'], one: (id: string) => ['car', id] },

  /**
   * The four taxonomy resources are served by one generic pair of screens
   * (`TaxonomyListPage` / `TaxonomyFormPage`), so their keys follow one rule.
   *
   * 🔴 The `'record'` segment is load-bearing for `carModel`: `CarModelListPage` reads
   * `['car-models', brandId]` — a list filtered by brand — which without the segment wears exactly
   * the same shape as one model's record key. The other three take the same shape for consistency,
   * because they are produced by the same generic config and a per-resource exception is how the
   * next reader reintroduces the collision.
   */
  carBrand: { list: ['car-brands'], one: (id: string) => ['car-brands', 'record', id] },
  carModel: { list: ['car-models'], one: (id: string) => ['car-models', 'record', id] },
  carCategory: { list: ['car-categories'], one: (id: string) => ['car-categories', 'record', id] },
  carFeature: { list: ['car-features'], one: (id: string) => ['car-features', 'record', id] },

  location: { list: ['admin-locations'], one: (id: string) => ['location', id] },
  pricingTemplate: { list: ['admin-pricing'], one: (id: string) => ['pricing-template', id] },
  fine: { list: ['fines'], one: (id: string) => ['fine', id] },

  /**
   * 🔴 Same collision as `carModel`, and the same fix: `MaintenanceListPage` reads
   * `['maintenance', carId]` — a list filtered by car — so one record cannot also be
   * `['maintenance', id]`.
   */
  maintenance: { list: ['maintenance'], one: (id: string) => ['maintenance', 'record', id] },

  discount: { list: ['discounts'], one: (id: string) => ['discount', id] },
  user: { list: ['admin-users'], one: (id: string) => ['user', id] },
  giftCertificate: { list: ['gift-certificates'], one: (id: string) => ['gift-certificate', id] },
  supplier: { list: ['suppliers'], one: (id: string) => ['supplier', id] },
  membership: { list: ['admin-memberships'], one: (id: string) => ['admin-membership', id] },
  review: { list: ['admin-reviews'], one: (id: string) => ['admin-review', id] },
  booking: { list: ['admin-bookings'], one: (id: string) => ['booking', id] },

  /**
   * 189: the fleet schedule — 🔴 **the resource 161's own registry missed.**
   *
   * `SchedulePage.tsx` reads `['schedule', day ?? 'today', plateQuery]` as a literal, so before this
   * entry a save that MOVED a car's occupancy had nothing to invalidate: the booking's own keys were
   * registered and the schedule's were not, and the operator returned to a day still drawn from the
   * pre-save cache. That is exactly the defect 161 exists to prevent, one resource short of its own
   * list — and it stayed invisible because a full browser reload clears the cache and hides it.
   *
   * `list` is `['schedule']`, the prefix every day and plate filter is built on, so invalidating it
   * reaches all of them through React Query's partial matching. `one(day)` is the day form; the day
   * is this resource's identifier, since the schedule has no record screen.
   */
  schedule: { list: ['schedule'], one: (day: string) => ['schedule', day] },

  // 207 (#313): the Roles screen — 5 fixed rows, no pagination, but still worth one shared cache
  // key so a permission-set save (`PATCH /roles/:id/permissions`) can invalidate both the list
  // (its `editable`/summary fields) and the detail it just edited.
  role: { list: ['admin-roles'], one: (id: string) => ['role', id] },
} as const satisfies Record<string, ResourceKeys>;

/**
 * The closed set of resources a save may name.
 *
 * 🔴 This union is what makes feature 161's FR-006 enforceable rather than merely documented:
 * `useSaveMutation` requires a `ResourceName`, so a form page that forgets to say what it saves
 * does not compile. A comment asking the author to remember is what was there before, and it is
 * what produced the defect nine times.
 */
export type ResourceName = keyof typeof RESOURCE_KEYS;
